import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { PickerSearchInput } from "./picker-search-input.tsx";
import {
  handlePickerSubmit,
  PICKER_HEADER_CLASS,
  pickerPillClass,
  usePickerKeyboard,
} from "./usePickerKeyboard.ts";

const QUICK_OPTIONS = ["Today", "Tomorrow", "This week", "Next week"];

/** Default selected index — "Next week" is index 3 in QUICK_OPTIONS. */
const DEFAULT_INDEX = 3;

/** All 7 days of the week, Sunday through Saturday. */
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface TimeDatePickerProps {
  onSelect: (timeDate: string) => void;
  /** Called when the highlighted option changes so the parent can preview connector words. */
  onPreview?: (timeDate: string) => void;
  /** Previously selected value — pre-selects the matching pill or pre-fills the free text input. */
  defaultValue?: string | null;
  /** Called when the user clicks "Pick specific times" to open the calendar availability picker. */
  onPickSpecificTimes?: () => void;
}

/** Quick-pick pill buttons + free text input for time/date selection. */
export function TimeDatePicker({
  onSelect,
  onPreview,
  defaultValue,
  onPickSpecificTimes,
}: TimeDatePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const allOptions = useMemo(() => [...QUICK_OPTIONS, ...WEEKDAYS], []);

  // If re-entering with a previous value, match it to a pill or fall back to free text.
  const defaultOptionIndex = useMemo(() => {
    if (!defaultValue) return -1;
    return allOptions.indexOf(defaultValue);
  }, [defaultValue, allOptions]);

  const [freeText, setFreeText] = useState(
    defaultOptionIndex < 0 && defaultValue ? defaultValue : "",
  );

  // Filter options based on typed text
  const filteredQuickOptions = useMemo(() => {
    const text = freeText.trim().toLowerCase();
    if (!text) return QUICK_OPTIONS;
    return QUICK_OPTIONS.filter((opt) => opt.toLowerCase().includes(text));
  }, [freeText]);

  const filteredWeekdays = useMemo(() => {
    const text = freeText.trim().toLowerCase();
    if (!text) return WEEKDAYS;
    return WEEKDAYS.filter((day) => day.toLowerCase().includes(text));
  }, [freeText]);

  const filteredAll = useMemo(
    () => [...filteredQuickOptions, ...filteredWeekdays],
    [filteredQuickOptions, filteredWeekdays],
  );

  const {
    selectedIndex,
    setSelectedIndex,
    handleKeyDown: handlePickerKeyDown,
  } = usePickerKeyboard({
    itemCount: filteredAll.length,
    onSelect: (index) => {
      const option = filteredAll[index];
      if (option) onSelect(option);
    },
    defaultIndex: defaultOptionIndex >= 0 ? defaultOptionIndex : DEFAULT_INDEX,
    orientation: "both",
  });

  // Notify parent of the highlighted option for real-time connector word preview.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire on selectedIndex change
  useEffect(() => {
    if (filteredAll[selectedIndex]) {
      onPreview?.(filteredAll[selectedIndex]);
    }
  }, [selectedIndex, filteredAll]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Arrow keys: delegate to the shared hook
      if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        handlePickerKeyDown(e);
        return;
      }

      // Enter/Tab: prefer a filtered option, fall back to custom text
      handlePickerSubmit(e, {
        filteredOptions: filteredAll,
        selectedIndex,
        freeText,
        onSelect,
      });
    },
    [freeText, filteredAll, selectedIndex, onSelect, handlePickerKeyDown],
  );

  return (
    <div ref={rootRef} tabIndex={-1} className="flex flex-col outline-none">
      {/* Search input */}
      <div className={PICKER_HEADER_CLASS} data-sticky-header>
        <div className="flex items-center gap-1 w-full">
          <div className="flex-1 min-w-0">
            <PickerSearchInput
              type="text"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onClear={() => setFreeText("")}
              placeholder="Type to filter or enter a time..."
              autoFocus
            />
          </div>
          {onPickSpecificTimes && (
            <button
              type="button"
              title="Select custom times"
              aria-label="Select custom times"
              className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                onPickSpecificTimes();
              }}
            >
              <CalendarDays className="size-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 px-3 py-2">
        {/* Quick picks */}
        {filteredQuickOptions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {filteredQuickOptions.map((opt) => {
              const idx = filteredAll.indexOf(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  className={pickerPillClass(idx === selectedIndex)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(opt);
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {/* Dynamic weekday pills */}
        {filteredWeekdays.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {filteredWeekdays.map((day) => {
              const idx = filteredAll.indexOf(day);
              return (
                <button
                  key={day}
                  type="button"
                  className={pickerPillClass(idx === selectedIndex)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(day);
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  {day.slice(0, 3)}
                </button>
              );
            })}
          </div>
        )}

        {/* Custom range pill */}
        {onPickSpecificTimes && (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              className={`${pickerPillClass(false)} inline-flex items-center whitespace-nowrap`}
              onMouseDown={(e) => {
                e.preventDefault();
                onPickSpecificTimes();
              }}
            >
              <CalendarDays className="size-3 mr-1 shrink-0" />
              Custom Range
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
